import {Injectable} from '@angular/core';
import {HttpBackend, HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MerchantResponseModel} from './merchantResponse.model';
import {TransactionsListResponseModel} from './transactionsListResponse.model';
import {TransactionDetailResponseModel} from './transactionDetailResponse.model';
import {RequestFilterModel} from '../../models/requestFilterModel';
import {CashcutListResponseModel} from './cashcutListResponse.model';
import {CashcutDetailResponseModel} from './cashcutDetailResponse.model';
import {ReportModel} from '../../models/report.model';
import {ReportFilterModel} from '../../models/reportFilter.model';
import {MerchantSetupResponseModel} from "../setup/merchantSetupResponseModel";
import {DeviceConfigurationResponseModel} from "./deviceConfigurationResponse.model";

@Injectable({
  providedIn: 'root',
})
export class MerchantService {
  constructor(private http: HttpClient, private httpBackend: HttpBackend) {
  }

  getPaycardDetails(): Observable<MerchantResponseModel> {
    return this.http.get<MerchantResponseModel>(`/v2/merchant`);
  }

  getTransactions(
    page: number,
    size: number,
    filter: RequestFilterModel
  ): Observable<TransactionsListResponseModel> {
    const fromDateObj = filter ? filter.fromDate : null;
    const toDateObj = filter ? filter.toDate : null;
    let fromDate;
    let toDate;

    if (fromDateObj) {
      fromDateObj.setHours(0, 0, 0, 0);
      fromDate = Math.floor(
        (fromDateObj.getTime() - fromDateObj.getTimezoneOffset() * 60 * 1000) /
        1000
      );
    }

    if (toDateObj) {
      toDateObj.setHours(23, 59, 59, 999);
      toDate = Math.floor(
        (toDateObj.getTime() - toDateObj.getTimezoneOffset() * 60 * 1000) / 1000
      );
    }

    return this.http.get<TransactionsListResponseModel>(
      `/v2/merchant/transaction`,
      {
        params: {
          page: page.toString(),
          size: size.toString(),
          pos: filter.pos,
          fromDate: fromDate ? fromDate : '',
          toDate: toDate ? toDate : '',
        },
      }
    );
  }

  getTransactionDetail(
    transactionId: string
  ): Observable<TransactionDetailResponseModel> {
    return this.http.get<TransactionDetailResponseModel>(
      `/v2/merchant/transaction/${transactionId}`
    );
  }

  getCashcuts(
    page: number,
    size: number,
    filter: RequestFilterModel
  ): Observable<CashcutListResponseModel> {
    const fromDateObj = filter ? filter.fromDate : null;
    const toDateObj = filter ? filter.toDate : null;
    let fromDate;
    let toDate;

    if (fromDateObj) {
      fromDateObj.setHours(0, 0, 0, 0);
      fromDate = Math.floor(
        (fromDateObj.getTime() - fromDateObj.getTimezoneOffset() * 60 * 1000) /
        1000
      );
    }

    if (toDateObj) {
      toDateObj.setHours(23, 59, 59, 999);
      toDate = Math.floor(
        (toDateObj.getTime() - toDateObj.getTimezoneOffset() * 60 * 1000) / 1000
      );
    }

    return this.http.get<CashcutListResponseModel>(`/v2/merchant/cashcut`, {
      params: {
        page: page.toString(),
        size: size.toString(),
        pos: filter.pos,
        fromDate: fromDate ? fromDate : '',
        toDate: toDate ? toDate : '',
      },
    });
  }

  getCashcutDetail(cashcutId: string): Observable<CashcutDetailResponseModel> {
    return this.http.get<CashcutDetailResponseModel>(
      `/v2/merchant/cashcut/${cashcutId}`
    );
  }

  getDeviceConfigurations(): Observable<DeviceConfigurationResponseModel[]> {
    return this.http.get<DeviceConfigurationResponseModel[]>(
      `/v2/merchant/devices/configuration`
    );
  }

  getCustomerSalesReport(filter: ReportFilterModel): Observable<ReportModel> {
    const fromDateObj = filter ? filter.fromDate : null;
    const toDateObj = filter ? filter.toDate : null;
    let fromDate;
    let toDate;

    if (fromDateObj) {
      fromDateObj.setHours(0, 0, 0, 0);
      fromDate = Math.floor(
        (fromDateObj.getTime() - fromDateObj.getTimezoneOffset() * 60 * 1000) /
        1000
      );
    }

    if (toDateObj) {
      toDateObj.setHours(23, 59, 59, 999);
      toDate = Math.floor((toDateObj.getTime() - toDateObj.getTimezoneOffset() * 60 * 1000) / 1000);
    }

    return this.http.get<ReportModel>(`/v2/merchant/reports/customersales`, {
      params: {
        ...(filter.posId && {pos: filter.posId}),
        ...(filter.posName && {posName: filter.posName}),
        ...(filter.cardId && {card: filter.cardId}),
        ...(filter.deviceId && {device: filter.deviceId}),
        ...(toDate && {toDate}),
        ...(fromDate && {fromDate}),
      },
    });
  }

  changeEmail(email: string): Observable<MerchantSetupResponseModel> {
    return this.http.put<MerchantSetupResponseModel>(
      `/v2/merchant/email`,
      {email},
    );
  }

  // this request is not intercepted by auth or api interceptor, so all information has to been given here
  changePin(
    cardId: string,
    oldPassword: string,
    password: string,
    api: string
  ): Observable<void> {
    const httpClient = new HttpClient(this.httpBackend);
    const authString = btoa(unescape(encodeURIComponent((`${cardId}:${oldPassword}`))));

    return httpClient.put<void>(
      `${api}/v2/merchant/password`,
      {password},
      {
        headers: {
          Authorization: `Basic ${authString}`,
        },
      }
    );
  }
}
